import { icon } from './icons';
import { logo } from './logo';

/* =====================================================================
   Diagrams (sections 7–17) and illustrative product UI (section 41, B5)
   ===================================================================== */
const LIFECYCLE = [
  'Intake',
  'Document Intelligence',
  'Case Workflow',
  'Evidence',
  'Settlement',
  'Client Experience',
  'Billing',
  'Reporting &amp; Portfolio Intelligence',
];
const INTEL = ['Rules', 'AI Models', 'Agents', 'APIs', 'Systems', 'Human Expertise'];
const srList = (title: string, items: readonly string[]): string =>
  `<div class="sr-only"><p>${title}</p><ol>${items.map((i) => `<li>${i}</li>`).join('')}</ol></div>`;

// Section 7 — executive platform visual
export function execVisual(): string {
  return `
  <figure class="exec rv">
    ${srList('Property tax lifecycle, connected at every stage by ReTax360 Intelligence and Orchestration (Rules, AI Models, Agents, APIs, Systems, Human Expertise), on one foundation of Clients, Communication, Data and Documents:', LIFECYCLE)}
    <div class="exec-band" aria-hidden="true">
      <div class="eb-title"><span class="dot run"></span>ReTax360 Intelligence &amp; Orchestration</div>
      <div class="eb-chips">${INTEL.map((t) => `<span>${t}</span>`).join('')}</div>
      <div class="eb-sub">Connects every stage</div>
    </div>
    <ol class="exec-stages" aria-hidden="true">
      ${LIFECYCLE.map((s, i) => `<li><span class="es-n">${String(i + 1).padStart(2, '0')}</span><span class="es-t">${s}</span></li>`).join('')}
    </ol>
    <div class="exec-base" aria-hidden="true"><b>One foundation</b><span>Clients · Communication · Data · Documents</span></div>
  </figure>`;
}

// Section 8 — three layers (Layer 1 top → Layer 3 bottom)
export function architecture({ compact = false } = {}): string {
  const vlinks = `<div class="link-v" aria-hidden="true"><span class="vw"><span class="trk"></span></span><span class="vw"><span class="trk"></span></span><span class="vw"><span class="trk"></span></span></div>`;
  const paths = [
    ['AI Models', 'sparkle'],
    ['AI Agents', 'zap'],
    ['Business Rules', 'code'],
    ['APIs &amp; Systems', 'plug'],
    ['Human Experts', 'userCheck'],
  ] as const;
  return `
  <figure class="rv">
    <div class="arch ${compact ? 'compact' : ''}" role="img" aria-label="Platform architecture in three layers. Layer 1, Your Workflow, configured around your organization: Intake, Documents, Appeals, Evidence, Deadlines, Settlement, Client Communication, Billing, Reporting. Layer 2, Intelligence and Orchestration, a central ReTax360 router with execution paths to AI Models, AI Agents, Business Rules, APIs and Systems, and Human Experts; sequence Route, Execute, Verify, Approve, Continue. Layer 3, One Foundation: Clients, Communication, Data, Documents, the shared foundation underneath every workflow.">
      <div class="layer l-work">
        <div class="lh"><div><div class="ln">Layer 1</div><h3>Your Workflow</h3></div><span class="lt">Configured around your organization</span></div>
        <div class="lchips">${['Intake', 'Documents', 'Appeals', 'Evidence', 'Deadlines', 'Settlement', 'Client Communication', 'Billing', 'Reporting'].map((t) => `<span>${t}</span>`).join('')}</div>
      </div>
      ${vlinks}
      <div class="layer l-orch">
        <div class="lh"><div><div class="ln">Layer 2</div><h3>Intelligence &amp; Orchestration</h3></div><span class="lt">Central ReTax360 router</span></div>
        <div class="router-row">
          <div class="router"><span class="mk">${logo('dark', true)}</span><span>ReTax360 router</span></div>
          <div class="paths">${paths.map(([t, i]) => `<span>${icon(i)}${t}</span>`).join('')}</div>
        </div>
        <div class="seq">${['Route', 'Execute', 'Verify', 'Approve', 'Continue'].map((t) => `<span>${t}</span>`).join('<i>→</i>')}</div>
      </div>
      ${vlinks}
      <div class="layer l-found">
        <div class="lh"><div><div class="ln">Layer 3</div><h3>One Foundation</h3></div><span class="lt">The shared foundation underneath every workflow</span></div>
        <div class="ftiles"><span>${icon('users')}Clients</span><span>${icon('message')}Communication</span><span>${icon('database')}Data</span><span>${icon('file')}Documents</span></div>
      </div>
      <p class="arch-cap">Every firm works a little differently. The foundation doesn't.</p>
    </div>
  </figure>`;
}

// Section 9 — smart routing
export function smartRouting(): string {
  const paths = [
    ['code', 'Rules / Code', 'Deterministic business rules'],
    ['sparkle', 'Specialized AI', 'Fast classification and extraction'],
    ['zap', 'Advanced AI', 'Reasoning and drafting'],
    ['plug', 'API / Enterprise System', 'Existing systems of record'],
    ['userCheck', 'Human Expert', 'Professional judgment'],
  ] as const;
  return `
  <figure class="rv">
    ${srList('Smart routing:', ['Task', 'ReTax360 router', 'Execution path: Rules / Code, Specialized AI, Advanced AI, API / Enterprise System, or Human Expert', 'Confidence and business rule check', 'Workflow continues or escalates for review'])}
    <div class="sroute" aria-hidden="true">
      <div class="sr-node task">${icon('inbox')}Task</div>
      <div class="sr-v"><span class="trk"></span></div>
      <div class="sr-node router">${logo('dark', true)}<span>ReTax360 router</span></div>
      <div class="sr-bus"></div>
      <div class="sr-paths">${paths.map(([i, t, s]) => `<div class="sr-path"><span class="ri">${icon(i)}</span><b>${t}</b><small>${s}</small></div>`).join('')}</div>
      <div class="sr-bus bottom"></div>
      <div class="sr-node check">${icon('shield')}Confidence + business rule check</div>
      <div class="sr-v"><span class="trk"></span></div>
      <div class="sr-node end">Workflow continues or escalates for review</div>
    </div>
  </figure>`;
}

// Section 10 — confidence-based escalation
export function escalation({ compact = false } = {}): string {
  const outs = [
    ['done', 'High confidence + permitted', 'Continue workflow'],
    ['run', 'Low confidence / exception', 'Human review'],
    ['human', 'Professional decision required', 'Assigned expert'],
  ];
  if (compact)
    return `
  <div class="esc-strip rv" role="group" aria-label="Confidence-based escalation: after a confidence and business rule check, high-confidence permitted work continues; low-confidence or exception work goes to human review; professional decisions go to the assigned expert, who can approve, modify or reject.">
    <span class="esc-lab">${icon('shield')}Confidence-based escalation</span>
    ${outs.map(([c, a, b]) => `<span class="esc-o ${c}"><b>${a}</b><small>→ ${b}</small></span>`).join('')}
    <span class="esc-amr">Approve · Modify · Reject</span>
  </div>`;
  return `
  <figure class="rv">
    ${srList('Confidence-based escalation:', ['AI, rule or data action', 'Confidence and business rule check', 'High confidence and permitted: continue workflow. Low confidence or exception: human review. Professional decision required: assigned expert.', 'Approve, modify or reject', 'Workflow continues'])}
    <div class="esc" aria-hidden="true">
      <div class="sr-node">${icon('zap')}AI / Rule / Data action</div>
      <div class="sr-v"><span class="trk"></span></div>
      <div class="sr-node check">${icon('shield')}Confidence + business rule check</div>
      <div class="esc-outs">${outs.map(([c, a, b]) => `<div class="esc-card ${c}"><b>${a}</b><span>→ ${b}</span></div>`).join('')}</div>
      <div class="sr-node amr">Approve · Modify · Reject</div>
      <div class="sr-v"><span class="trk"></span></div>
      <div class="sr-node end">Workflow continues</div>
    </div>
    <figcaption class="rcap">Example: conflicting owner name → low confidence / exception → human review (Morgan Lee) → approve · modify · reject. Illustrative.</figcaption>
  </figure>`;
}

// Section 17 — workflow trace
export function traceCard(): string {
  const rows = [
    ['09:14', 'Task received', 'Assessment notice'],
    ['09:14', 'Route selected', 'Document Intelligence'],
    ['09:15', 'Output', '14 fields extracted'],
    ['09:15', 'Confidence / verification', '0.97 · rule check passed'],
    ['09:15', 'Human action', 'Not required'],
    ['09:15', 'Final workflow state', 'Case updated · {{CASE}}'],
  ];
  return `
  <div class="panel trace" role="img" aria-label="Illustrative product UI: workflow trace. 09:14 task received, assessment notice. 09:14 route selected, Document Intelligence. 09:15 output, 14 fields extracted. 09:15 confidence 0.97, rule check passed. 09:15 human action not required. Final state, case {{CASE}} updated.">
    <div class="panel-head"><span><b>Workflow trace</b> · {{CASE}}</span><span class="live" style="color:var(--st-done)"><span class="dot"></span>Logged</span></div>
    <ol class="trace-rows">${rows.map(([t, a, b]) => `<li><span class="tt">${t}</span><span class="ta">${a}</span><span class="tb">${b}</span></li>`).join('')}</ol>
    <p class="cap">Illustrative product UI · fictional data</p>
  </div>`;
}
